import { API_BASE_URL } from '../api';
import type { 
  BackendService, 
  ServiceCategory, 
  BookingRequest, 
  BookingResponse,
  AvailableSlot
} from '../types/api';

/**
 * Récupère la liste des catégories de services depuis le backend
 */
export const getServiceCategories = async (): Promise<ServiceCategory[]> => {
  const response = await fetch(`${API_BASE_URL}/api/categories`);
  if (!response.ok) {
    throw new Error('Failed to fetch service categories');
  }
  return response.json();
};

/**
 * Récupère la liste des services depuis le backend
 */
export const getServices = async (
  categoryId?: number,
  bookableOnly: boolean = false,
): Promise<BackendService[]> => {
  const params = new URLSearchParams();
  if (categoryId) params.set('category_id', String(categoryId));
  if (bookableOnly) params.set('bookable_only', 'true');
  const qs = params.toString();
  const url = qs ? `${API_BASE_URL}/api/services?${qs}` : `${API_BASE_URL}/api/services`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch services');
  }
  return response.json();
};

/**
 * Récupère la liste des services pour une catégorie donnée
 */
export const getServicesByCategorySlug = async (
  categorySlug: string,
  bookableOnly: boolean = false,
): Promise<BackendService[]> => {
  const url = bookableOnly
    ? `${API_BASE_URL}/api/categories/${categorySlug}/services?bookable_only=true`
    : `${API_BASE_URL}/api/categories/${categorySlug}/services`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch services by category');
  }
  return response.json();
};

/**
 * Récupère un service spécifique
 */
export const getService = async (serviceId: number): Promise<BackendService> => {
  const response = await fetch(`${API_BASE_URL}/api/services/${serviceId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch service');
  }
  return response.json();
};

/**
 * Récupère les créneaux disponibles pour un service et une date
 */
export async function getAvailableSlots(
  serviceId: number, 
  date: string
): Promise<AvailableSlot[]> {
  const response = await fetch(
    `${API_BASE_URL}/api/slots?service_id=${serviceId}&date=${date}`
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch available slots');
  }
  
  return response.json();
}

/**
 * Crée une nouvelle réservation
 */
export async function createBooking(
  bookingData: BookingRequest
): Promise<BookingResponse> {
  const response = await fetch(`${API_BASE_URL}/api/bookings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(bookingData)
  });
  
  if (!response.ok) {
    throw new Error('Failed to create booking');
  }
  
  return response.json();
}

/**
 * Récupère la liste des praticiens
 */
export async function getPractitioners(): Promise<any[]> {
  const response = await fetch(`${API_BASE_URL}/api/practitioners`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch practitioners');
  }
  
  return response.json();
}
